import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Search, Headphones, Phone, Mail, Building2,
  Calendar, Trash2, AlertTriangle, ShieldCheck, ExternalLink, Copy, Check,
} from "lucide-react";
import SEO from "../components/SEO";
import { API } from "../apiBase";

const STATUSES = [
  { v: "pending", label: "En attente", color: "border-[rgba(255,170,80,0.4)] bg-[rgba(255,170,80,0.08)] text-[#ffce99]" },
  { v: "accepted", label: "Acceptée", color: "border-[rgba(127,179,255,0.4)] bg-[rgba(127,179,255,0.08)] text-[#0284C7]" },
  { v: "session_active", label: "Session active", color: "border-[rgba(110,231,197,0.5)] bg-[rgba(110,231,197,0.10)] text-[var(--mc-mint)]" },
  { v: "closed", label: "Terminée", color: "border-[rgba(180,180,180,0.4)] bg-[rgba(180,180,180,0.06)] text-[#bbb]" },
  { v: "cancelled", label: "Annulée", color: "border-[rgba(255,120,120,0.4)] bg-[rgba(255,120,120,0.06)] text-[#DC2626]" },
];
const META = Object.fromEntries(STATUSES.map((s) => [s.v, s]));

export default function AdminSupport() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(null); // {id, session_link}

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/support/remote-requests`);
      setItems(data || []);
    } catch (e) {
      setErr("Impossible de charger les demandes.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status, session_link) => {
    try {
      await axios.put(`${API}/support/remote-requests/${id}/status`, { status, session_link });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status, session_link: session_link ?? x.session_link } : x)));
      setEditing(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Mise à jour impossible.");
    }
  };

  const deleteOne = async (id, who) => {
    if (!window.confirm(`Supprimer définitivement la demande de « ${who} » ?`)) return;
    try {
      await axios.delete(`${API}/support/remote-requests/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { /* noop */ }
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (filter !== "all") arr = arr.filter((x) => (x.status || "pending") === filter);
    if (!query.trim()) return arr;
    const q = query.toLowerCase().trim();
    return arr.filter((x) =>
      (x.full_name || "").toLowerCase().includes(q) ||
      (x.email || "").toLowerCase().includes(q) ||
      (x.phone || "").toLowerCase().includes(q) ||
      (x.organization || "").toLowerCase().includes(q) ||
      (x.reason || "").toLowerCase().includes(q)
    );
  }, [items, filter, query]);

  const stats = useMemo(() => {
    const out = { total: items.length };
    for (const s of STATUSES) out[s.v] = items.filter((x) => (x.status || "pending") === s.v).length;
    return out;
  }, [items]);

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid pt-28 pb-16 px-5 lg:px-8" data-testid="page-admin-support">
      <SEO title="Support à distance — Admin MetaConnect" description="Gestion des demandes de prise en main à distance." canonical="/admin/support" noIndex />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12px] text-[var(--mc-muted)] hover:text-[#0F2B5B] transition">
            <ArrowLeft size={14} /> Retour admin
          </Link>
          <button
            onClick={load}
            data-testid="admin-support-refresh"
            className="inline-flex items-center gap-1.5 text-[12px] text-[#0284C7] hover:text-[#0F2B5B] transition"
          >
            <RefreshCw size={14} /> Rafraîchir
          </button>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7]">
            <Headphones size={18} />
          </span>
          <div>
            <h1 className="font-display text-[#0F2B5B] text-2xl">Support à distance</h1>
            <p className="text-[13px] text-[var(--mc-muted)]">Demandes de prise en main client avec consentement RGPD</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-6 mb-6">
          <StatCard label="Total" value={stats.total} onClick={() => setFilter("all")} active={filter === "all"} />
          {STATUSES.map((s) => (
            <StatCard key={s.v} label={s.label} value={stats[s.v]} onClick={() => setFilter(s.v)} active={filter === s.v} colorClass={s.color} />
          ))}
        </div>

        {/* Search */}
        <div className="mc-card p-4 mb-5">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom, email, téléphone…"
              data-testid="admin-support-search"
              className="mc-input pl-9"
            />
          </div>
        </div>

        {err && (
          <div className="mc-card p-4 mb-4 border-[#FCA5A5] flex items-center gap-3">
            <AlertTriangle size={16} className="text-[#DC2626]" />
            <p className="text-[13px] text-[#334155]">{err}</p>
          </div>
        )}

        {loading && (
          <div className="mc-card p-8 text-center text-[14px] text-[var(--mc-muted)]">Chargement…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="mc-card p-10 text-center">
            <Headphones size={32} className="mx-auto text-[#0284C7] mb-3" />
            <p className="text-[14px] text-[#334155]">Aucune demande de support à distance pour le moment.</p>
            <p className="text-[12px] text-[var(--mc-muted)] mt-1">Les nouvelles demandes apparaîtront ici en temps réel.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((it) => {
              const status = it.status || "pending";
              const m = META[status] || META.pending;
              return (
                <div key={it.id} className="mc-card p-5" data-testid={`support-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-display text-[#0F2B5B] text-[16px]">{it.full_name}</h3>
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${m.color}`}>
                          {m.label}
                        </span>
                        {it.consent_remote && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full border border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.08)] text-[var(--mc-mint)]">
                            <ShieldCheck size={10} /> Consentement RGPD
                          </span>
                        )}
                      </div>

                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] text-[var(--mc-muted)]">
                        <div className="flex items-center gap-2">
                          <Phone size={12} />
                          <a href={`tel:${it.phone}`} className="text-[#0284C7] hover:text-[#0F2B5B]">{it.phone}</a>
                          <button
                            onClick={() => copyToClipboard(it.phone, `${it.id}-tel`)}
                            className="text-[var(--mc-muted)] hover:text-[#0F2B5B]"
                            title="Copier"
                            data-testid={`copy-phone-${it.id}`}
                          >
                            {copied === `${it.id}-tel` ? <Check size={12} className="text-[var(--mc-mint)]" /> : <Copy size={11} />}
                          </button>
                        </div>
                        <div className="flex items-center gap-2"><Mail size={12} /><a href={`mailto:${it.email}`} className="hover:text-[#0F2B5B]">{it.email}</a></div>
                        {it.organization && (
                          <div className="flex items-center gap-2"><Building2 size={12} />{it.organization}</div>
                        )}
                        <div className="flex items-center gap-2"><Calendar size={12} />{new Date(it.created_at).toLocaleString("fr-FR")}</div>
                      </div>

                      <div className="mt-3 p-3 rounded-lg bg-[rgba(127,179,255,0.05)] border border-[rgba(127,179,255,0.12)]">
                        <p className="text-[12.5px] text-[#334155] leading-relaxed whitespace-pre-wrap">{it.reason}</p>
                      </div>

                      {/* Session link management */}
                      <div className="mt-3 p-3 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(127,179,255,0.08)]">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] uppercase tracking-[0.18em] text-[#0284C7]">Lien de session</span>
                          {it.session_link && (
                            <button
                              onClick={() => copyToClipboard(it.session_link, `${it.id}-link`)}
                              className="text-[11px] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1"
                              data-testid={`copy-link-${it.id}`}
                            >
                              {copied === `${it.id}-link` ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
                            </button>
                          )}
                        </div>
                        {editing?.id === it.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="url"
                              value={editing.session_link || ""}
                              onChange={(e) => setEditing({ ...editing, session_link: e.target.value })}
                              placeholder="https://support.metaconnect.fr/session/..."
                              data-testid={`edit-link-input-${it.id}`}
                              className="mc-input text-[12px] flex-1"
                            />
                            <button
                              onClick={() => updateStatus(it.id, "accepted", editing.session_link)}
                              data-testid={`save-link-${it.id}`}
                              className="btn-primary px-3 py-2 rounded-lg text-[11px]"
                            >
                              Enregistrer
                            </button>
                            <button onClick={() => setEditing(null)} className="text-[11px] text-[var(--mc-muted)] hover:text-[#0F2B5B] px-2">Annuler</button>
                          </div>
                        ) : it.session_link ? (
                          <a
                            href={it.session_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[12px] text-[#0284C7] hover:text-[#0F2B5B] break-all"
                          >
                            <ExternalLink size={11} /> {it.session_link}
                          </a>
                        ) : (
                          <button
                            onClick={() => setEditing({ id: it.id, session_link: "" })}
                            data-testid={`add-link-${it.id}`}
                            className="text-[11.5px] text-[#0284C7] hover:text-[#0F2B5B] underline"
                          >
                            + Ajouter un lien de session (MeshCentral / RustDesk / TeamViewer…)
                          </button>
                        )}
                      </div>

                      {/* Audit log */}
                      {(it.consent_ip || it.consent_user_agent) && (
                        <details className="mt-2">
                          <summary className="text-[10px] uppercase tracking-[0.16em] text-[var(--mc-muted)] cursor-pointer hover:text-[#0F2B5B]">
                            Audit RGPD
                          </summary>
                          <div className="mt-1 text-[11px] text-[var(--mc-muted)] font-mono space-y-0.5 pl-2 border-l border-[rgba(127,179,255,0.15)]">
                            {it.consent_ip && <div>IP: {it.consent_ip}</div>}
                            {it.consent_user_agent && <div className="break-all">UA: {it.consent_user_agent}</div>}
                          </div>
                        </details>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 min-w-[160px]">
                      <select
                        value={status}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        data-testid={`status-select-${it.id}`}
                        className="mc-input text-[12px]"
                      >
                        {STATUSES.map((s) => (
                          <option key={s.v} value={s.v}>{s.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => deleteOne(it.id, it.full_name)}
                        data-testid={`delete-${it.id}`}
                        className="inline-flex items-center justify-center gap-1.5 text-[11px] text-[#DC2626] hover:text-[#0F2B5B] border border-[rgba(252,165,165,0.3)] hover:border-[#FCA5A5] rounded-lg py-2 transition"
                      >
                        <Trash2 size={11} /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, onClick, active, colorClass }) {
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`text-left rounded-lg border px-3 py-2.5 transition ${
        active
          ? "border-[var(--mc-blue-400)] bg-[rgba(45,123,255,0.12)]"
          : colorClass
          ? colorClass
          : "border-[rgba(127,179,255,0.18)] bg-[rgba(127,179,255,0.03)] hover:border-[var(--mc-blue-400)]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--mc-muted)]">{label}</div>
      <div className="text-[#0F2B5B] font-display text-[20px] leading-none mt-1">{value}</div>
    </button>
  );
}
